/* global $:true*/ import _ from 'lodash';

const DEBUG = false;

function log(...args) {
  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.log(...args);
  }
}

function pagingCodec(options) {
  return function (req, res) {
    let items = res.data,
      from = req.start,
      to = req.start + items.length,
      count = items.length,
      total = parseInt(res.total_count, 10);

    return {
      items: items,
      from: from,
      to: to,
      count: count,
      total: total,
    };
  };
}

/**
 * data provider api:
 *   getItem(),
 *   getLength(),   // the length if the data is array
 *   getItemMetadata()
 */

export function RemoteData(options) {
  let codec = _.get(options, 'codec', null);
  if (codec == null) {
    codec = pagingCodec();
  }

  const rowAdapter = _.isFunction(options.rowAdapter) && options.rowAdapter;

  let data = { length: 0 },
    reqLog = [],
    _rc = -1, // logger request counter
    _grid,
    _prevViewport,
    sortCols = [],
    _url = options.url,
    _query = options.query || {},
    reqTimeoutID,
    req; // the ajax request

  // public API
  return {
    // plugin API
    init,
    destroy,
    // data array
    data: data,
    // methods
    clear,
    reload,
    ensureData,
    loadAll,
    initViewport,
    // query params
    setQuery,
    addQuery,
    clearQuery,
    // edit
    updateItem,
    removeItem,
  };

  function clear() {
    for (let key in data) {
      delete data[key];
    }
    data.length = 0;
  }

  function updateItem(id, obj) {
    if (rowAdapter) {
      rowAdapter(obj);
    }

    for (let key in data) {
      if (data[key] && data[key].id == id) {
        data[key] = obj;
      }
    }
  }

  function removeItem(id) {
    let pos = null;
    for (let row in data) {
      if (data[row] && data[row].id == id) {
        pos = row;
        break;
      }
    }

    if (pos != null) {
      let rows = _.chain(data)
        .keys()
        .filter((p) => p != 'length')
        .map(_.parseInt)
        .value()
        .sort();
      // , lastPos = rows[rows.length - 1]

      _.each(rows, (row) => {
        if (pos <= row) {
          data[row] = data[row + 1];
        }
      });

      data.length -= 1;
    }
  }

  function reload() {
    ensureData(true);
  }

  function setQuery(obj) {
    _query = obj;
  }

  function addQuery(obj) {
    _query = _.extend(_query, obj);
  }

  function clearQuery() {
    setQuery({});
  }

  function getQueryParams(defaults) {
    if (_.isFunction(options.queryParams)) {
      return _.extend({}, defaults, options.queryParams(defaults));
    }
    if (_.isObject(options.queryParams)) {
      return _.extend({}, defaults, options.queryParams);
    }
    return defaults;
  }

  function loadAll() {
    // building query
    let queryParams = getQueryParams(_.extend({ start: 0, size: -1 }, _query));

    if (sortCols && sortCols.length) {
      let order = '',
        current;
      for (let i = 0; i < sortCols.length; i++) {
        current = sortCols[i];
        order += (i > 0 ? ',' : '') + current.field;
        if (
          i === sortCols.length - 1 ||
          (i < sortCols.length - 1 && sortCols[i + 1].dir !== current.dir)
        ) {
          order += current.dir === 1 ? '+asc' : '+desc';
        }
      }
      queryParams.sort = order;
    }

    let url =
      _url +
      (_url.indexOf('?') > -1 ? '&' : '?') +
      $.param(queryParams).replace(/%24/g, '$');

    return $.ajax({
      url: url,
      contentType: 'application/json',
      dataType: 'json',
    }).then((res) => {
      let items = res.data;
      if (rowAdapter) {
        _.each(items, options.rowAdapter);
      }
      return items;
    });
  }

  function initViewport(viewport) {
    _prevViewport = viewport;
  }

  /**
   * from, to, ajaxOptions, force
   */
  function ensureData(force = false) {
    const viewport = _grid.getViewport();
    _rc += 1;

    log(`[ensureData][${_rc}] viewport =`, viewport);

    if (
      !force &&
      _prevViewport &&
      _prevViewport.top == viewport.top &&
      _prevViewport.bottom == viewport.bottom
    ) {
      log(`[ensureData][${_rc}] stop: unchanged viewport`);
      return;
    }

    let vpsize = (viewport.bottom - viewport.top) * 10,
      vpcenter = viewport.bottom - (viewport.bottom - viewport.top) / 2,
      start = Math.max(0, viewport.top - vpsize),
      end = viewport.bottom + vpsize,
      size;

    log(
      `[ensureData][${_rc}] start`,
      start,
      '; end',
      end,
      '; data.length',
      data.length,
    );

    if (_.isNaN(end)) {
      log(`[ensureData][${_rc}] stop: end is NaN`);
      return;
    }

    if (_.isUndefined(vpcenter)) {
      log(`[ensureData][${_rc}] warn: vpcenter is undefined`);
      // do nothing
    } else if (!force && _.isUndefined(data[start])) {
      log(`[ensureData][${_rc}] no start data, scrolling up`);
      // scrolling up
      for (let i = end; i >= start; i--) {
        if (_.isUndefined(data[i])) {
          end = i;
          break;
        }
      }
      start = Math.max(0, end - vpsize * 2);
      log(`[ensureData][${_rc}] start`, start, '; end', end);
    } else if (!force && _.isUndefined(data[end])) {
      log(`[ensureData][${_rc}] no end data, scrolling down`);
      // scrolling down
      for (let i = start; i <= end; i++) {
        if (_.isUndefined(data[i])) {
          start = i;
          break;
        }
      }
      end = start + vpsize * 2;
      log(`[ensureData][${_rc}] start`, start, '; end', end);
    } else if (!force) {
      log(`[ensureData][${_rc}] stop: no force`);
      return;
    }

    if (data.length == 0) {
      log(`[ensureData][${_rc}] no data at all, query from start index`);
      // if we have no data, then we must query from pos 0
      start = 0;
      end = start + vpsize;
      log(`[ensureData][${_rc}] start`, start, '; end', end);
    }

    size = end - start + 1;

    let req = _.last(reqLog);

    // it there's a running request we cancel it.
    if (_.get(req, 'status') == 'running') {
      log(`[ensureData][${_rc}] cancelling running request`);
      req.jqXHR.abort();
      for (let i = req.start; i <= req.end; i++) {
        if (data[i] == null) {
          delete data[i];
        }
      }
    }

    if (data.length && start >= data.length) {
      log(
        `[ensureData][${_rc}] stop: start >= data.length`,
        start,
        data.length,
      );
      return;
    }

    // building query
    let queryParams = getQueryParams(_.extend({ start, size }, _query));

    if (sortCols && sortCols.length) {
      let order = '',
        current;
      for (let i = 0; i < sortCols.length; i++) {
        current = sortCols[i];
        order += (i > 0 ? ',' : '') + current.field;
        if (
          i === sortCols.length - 1 ||
          (i < sortCols.length - 1 && sortCols[i + 1].dir !== current.dir)
        ) {
          order += current.dir === 1 ? '+asc' : '+desc';
        }
      }
      queryParams.sort = order;
    }

    log(`[ensureData][${_rc}] queryParams: `, queryParams);

    // ------------------------------
    // hack to ensure not bombing server with the same requests
    if (
      !force &&
      _.isEqual(queryParams, _.get(_.last(reqLog), 'queryParams'))
    ) {
      log('[ensureData] stop: queryParams unchanged, no force');
      return false;
    }
    reqLog.push({ queryParams });
    // ------------------------------

    let url =
      _url +
      (_url.indexOf('?') > -1 ? '&' : '?') +
      $.param(queryParams).replace(/%24/g, '$');

    if (reqTimeoutID !== null) {
      clearTimeout(reqTimeoutID);
    }

    reqTimeoutID = setTimeout(function () {
      emit('loading', { url });

      for (let i = start; i <= end; i++) {
        // null indicates 'requested but not available yet'
        data[i] = null;
      }

      log(`[ensureData][${_rc}] make request:`, url);
      req = $.ajax(
        {
          url: url,
          contentType: 'application/json',
          dataType: 'json',
          success: onRequestSuccess,
          error: (err) => onRequestError(start, size, url, err),
        } /* , ajaxOptions)*/,
      );

      req.start = start;
      req.end = end;
      req.size = size;
    }, 50);
  }

  function onRequestError(start, size, url, error) {
    reqTimeoutID = null;
    if (error && error.statusText == 'abort') {
      return emit('abort', { start, size, url, error });
    }
    emit('error', { start, size, url, error });
  }

  function onRequestSuccess(res) {
    reqTimeoutID = null;
    // Solution to keep the data array bounded to pagesize + window:
    // Call the clear method to have only 2*PAGESIZE elements in the data
    // array at any given point
    // clear();
    let tx = options.adapter.dataLoaded(req, res);
    data.length = tx.total;

    for (let i = 0; i < tx.count; i++) {
      if (options.rowAdapter) {
        options.rowAdapter(tx.items[i]);
      }
      data[tx.from + i] = tx.items[i];
      data[tx.from + i].index = tx.from + i;
    }

    req = null;

    let args = {
      from: tx.from,
      to: tx.to,
      count: tx.count,
      total: tx.total,
    };
    emit('loaded', args);
  }

  /*
   *  Plugin
   */
  function init(slickgrid) {
    _grid = slickgrid;
    slickgrid.onViewportChanged.subscribe(onViewportChanged);
    slickgrid.onSort.subscribe(onGridSort);
  }

  function destroy() {
    _grid.onSort.unsubscribe(onGridSort);
    _grid.onViewportChanged.unsubscribe(onViewportChanged);
  }

  function onViewportChanged(ev) {
    ensureData();
  }

  function onGridSort(e, args) {
    let result = [],
      column,
      cols = _grid.getColumns();

    _.each(_grid.getSortColumns(), (col, k) => {
      column = cols[_grid.getColumnIndex(col.columnId)];

      let dir = col.sortAsc ? 1 : -1;
      if (column.order_fields) {
        _.each(column.order_fields, (field, k) => result.push({ field, dir }));
      } else {
        result.push({ field: column.field, dir });
      }
    });

    sortCols = result;
    clear();
    ensureData();
  }

  // function updateGrid({ from, to }){
  //   for (var i = from; i <= to; i++) {
  //     _grid.invalidateRow(i)
  //   }
  //   _grid.updateRowCount()
  //   _grid.render()
  // }

  function emit(event, args) {}
}
